import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  SEO_DOMAIN,
  OG_LOCALE_MAP,
  validLanguages,
  pathToPageKey,
  buildCanonicalUrl,
  buildLocalizedUrl,
  parsePathname,
  updateMetaTag,
  updateLinkTag,
  injectHreflangTags,
  updateDocumentLang,
  type PageKey,
} from "@/lib/seo";

export function usePageMeta() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    // Parse current path
    const { lang: currentLang, pagePath, pageKey } = parsePathname(
      location.pathname,
      i18n.language
    );

    // Get translated title and description
    const title = t(`seo.${pageKey}.title`, { defaultValue: "" });
    const description = t(`seo.${pageKey}.description`, { defaultValue: "" });

    // Build canonical URL
    const canonicalUrl = buildCanonicalUrl(currentLang, pagePath);

    // Get OG locale
    const ogLocale = OG_LOCALE_MAP[currentLang] || "en_US";

    // Update document title
    if (title) {
      document.title = title;
    }

    // Update meta description
    updateMetaTag('meta[name="description"]', "content", description);

    // Update canonical URL
    updateLinkTag("canonical", canonicalUrl);

    // Update OG tags
    updateMetaTag('meta[property="og:url"]', "content", canonicalUrl);
    updateMetaTag('meta[property="og:title"]', "content", title);
    updateMetaTag('meta[property="og:description"]', "content", description);
    updateMetaTag('meta[property="og:locale"]', "content", ogLocale);

    // Update Twitter tags
    updateMetaTag('meta[name="twitter:title"]', "content", title);
    updateMetaTag('meta[name="twitter:description"]', "content", description);

    // Inject dynamic hreflang tags
    injectHreflangTags(pagePath);

    // Update html lang and dir attributes
    updateDocumentLang(currentLang);
  }, [location.pathname, t, i18n.language]);
}
